import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Handshake, 
  Building2, 
  DollarSign, 
  Plus, 
  Search,
  Calendar,
  TrendingUp,
  MoreVertical,
  Pencil,
  Trash2,
  ArrowRight,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';
import SARSymbol from '../components/SARSymbol';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency display helper
const CurrencyValue = ({ value, symbol, isSAR, size = 14 }) => {
  if (isSAR) {
    return (
      <span className="inline-flex items-center gap-1">
        <SARSymbol size={size} />
        <span>{value}</span>
      </span>
    );
  }
  return <span>{symbol} {value}</span>;
};

const Deals = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol, isSAR } = useSettings();
  const [deals, setDeals] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState({});
  const [viewMode, setViewMode] = useState('pipeline');

  const stages = [
    { id: 'qualification', label: isRTL ? 'التأهيل' : 'Qualification', color: 'bg-slate-500' },
    { id: 'proposal', label: isRTL ? 'العرض' : 'Proposal', color: 'bg-blue-500' },
    { id: 'negotiation', label: isRTL ? 'التفاوض' : 'Negotiation', color: 'bg-amber-500' },
    { id: 'closed_won', label: isRTL ? 'فاز' : 'Closed Won', color: 'bg-emerald-500' },
    { id: 'closed_lost', label: isRTL ? 'خسر' : 'Closed Lost', color: 'bg-red-500' }
  ];

  useEffect(() => {
    fetchDeals();
    fetchClients();
    fetchContacts();
  }, []);

  const fetchDeals = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/deals`);
      setDeals(res.data);
    } catch (err) {
      console.error(err);
      toast.error(isRTL ? 'فشل تحميل الصفقات' : 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/clients`);
      setClients(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/contacts`);
      setContacts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingDeal) {
        await axios.put(`${API_URL}/api/crm/deals/${editingDeal.id}`, formData);
        toast.success(isRTL ? 'تم تحديث الصفقة' : 'Deal updated');
      } else {
        await axios.post(`${API_URL}/api/crm/deals`, formData);
        toast.success(isRTL ? 'تم إنشاء الصفقة' : 'Deal created');
      }
      setShowDialog(false);
      setEditingDeal(null);
      setFormData({});
      fetchDeals();
    } catch (err) {
      toast.error(isRTL ? 'فشل حفظ الصفقة' : 'Failed to save deal');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRTL ? 'هل أنت متأكد من حذف هذه الصفقة؟' : 'Are you sure you want to delete this deal?')) {
      try {
        await axios.delete(`${API_URL}/api/crm/deals/${id}`);
        toast.success(isRTL ? 'تم حذف الصفقة' : 'Deal deleted');
        fetchDeals();
      } catch (err) {
        toast.error(isRTL ? 'فشل حذف الصفقة' : 'Failed to delete deal');
      }
    }
  };

  const updateStage = async (dealId, newStage) => {
    try {
      await axios.put(`${API_URL}/api/crm/deals/${dealId}`, { stage: newStage });
      toast.success(isRTL ? 'تم تحديث المرحلة' : 'Stage updated');
      fetchDeals();
    } catch (err) {
      toast.error(isRTL ? 'فشل تحديث المرحلة' : 'Failed to update stage');
    }
  };

  const openEdit = (deal) => {
    setEditingDeal(deal);
    setFormData({
      ...deal,
      expected_close_date: deal.expected_close_date ? deal.expected_close_date.split('T')[0] : ''
    });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingDeal(null);
    setFormData({ stage: 'qualification', probability: 20, currency: 'USD' });
    setShowDialog(true);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || (isRTL ? 'بدون عميل' : 'No Client');
  };

  const pipelineValue = deals
    .filter(d => !['closed_won', 'closed_lost'].includes(d.stage))
    .reduce((sum, d) => sum + (d.value || 0), 0);
  
  const wonValue = deals
    .filter(d => d.stage === 'closed_won')
    .reduce((sum, d) => sum + (d.value || 0), 0);

  const getDealsForStage = (stageId) => deals.filter(d => d.stage === stageId);
  const getStageValue = (stageId) => getDealsForStage(stageId).reduce((sum, d) => sum + (d.value || 0), 0);

  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('pages.deals.title')}</h1>
          <p className="text-muted-foreground">{t('pages.deals.subtitle')}</p>
        </div>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Button variant={viewMode === 'pipeline' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('pipeline')}>
            {isRTL ? 'خط الأنابيب' : 'Pipeline'}
          </Button>
          <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('list')}>
            {isRTL ? 'قائمة' : 'List'}
          </Button>
          <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse mr-2' : 'ml-2'}`}>
            <Plus className="h-4 w-4" /> {t('pages.deals.addDeal')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{deals.length}</div>
            <div className="text-sm text-muted-foreground">{isRTL ? 'إجمالي الصفقات' : 'Total Deals'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600"><CurrencyValue value={pipelineValue.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div>
            <div className="text-sm text-muted-foreground">{t('dashboard.pipelineValue')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-emerald-600"><CurrencyValue value={wonValue.toLocaleString()} symbol={currencySymbol} isSAR={isSAR} /></div>
            <div className="text-sm text-muted-foreground">{isRTL ? 'قيمة الصفقات الفائزة' : 'Won Value'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">
              {deals.length > 0 ? Math.round(deals.filter(d => d.stage === 'closed_won').length / deals.length * 100) : 0}%
            </div>
            <div className="text-sm text-muted-foreground">{t('dashboard.winRate')}</div>
          </CardContent>
        </Card>
      </div>

      {viewMode === 'pipeline' ? (
        /* Pipeline View */
        <div className={`grid grid-cols-5 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
          {stages.filter(s => s.id !== 'closed_lost').map((stage) => (
            <div key={stage.id} className="space-y-3">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`h-2 w-2 rounded-full ${stage.color}`} />
                  <span className="font-medium text-sm">{stage.label}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  <CurrencyValue value={getStageValue(stage.id).toLocaleString()} symbol={currencySymbol} isSAR={isSAR} size={12} />
                </span>
              </div>
              
              <div className="space-y-2 min-h-[200px]">
                {getDealsForStage(stage.id).map((deal) => (
                  <Card key={deal.id} className="cursor-pointer hover:border-primary/50 transition-all">
                    <CardContent className="p-3">
                      <div className={`flex justify-between items-start mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <h4 className={`font-medium text-sm line-clamp-1 ${isRTL ? 'text-right' : ''}`}>{deal.title}</h4>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(deal)}>
                              <Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {stages.filter(s => s.id !== deal.stage).map(s => (
                              <DropdownMenuItem key={s.id} onClick={() => updateStage(deal.id, s.id)}>
                                <ArrowRight className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {isRTL ? 'نقل إلى' : 'Move to'} {s.label}
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(deal.id)}>
                              <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      <div className={`text-xs text-muted-foreground mb-2 ${isRTL ? 'text-right' : ''}`}>
                        {getClientName(deal.client_id)}
                      </div>
                      
                      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="font-mono font-semibold text-sm">
                          <CurrencyValue value={deal.value?.toLocaleString() || 0} symbol={currencySymbol} isSAR={isSAR} size={12} />
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {deal.probability}%
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {getDealsForStage(stage.id).length === 0 && (
                  <div className="border-2 border-dashed rounded-lg p-4 text-center text-muted-foreground text-xs">
                    {isRTL ? 'لا توجد صفقات' : 'No deals'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="border-b">
                <tr className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : 'text-left'}`}>
                  <th className="p-4">{isRTL ? 'الصفقة' : 'Deal'}</th>
                  <th className="p-4">{isRTL ? 'العميل' : 'Client'}</th>
                  <th className="p-4">{t('pages.deals.value')}</th>
                  <th className="p-4">{t('pages.deals.stage')}</th>
                  <th className="p-4">{t('pages.deals.probability')}</th>
                  <th className="p-4">{t('pages.deals.expectedClose')}</th>
                  <th className="p-4 w-[50px]"></th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => (
                  <tr key={deal.id} className={`border-b last:border-0 hover:bg-muted/50 ${isRTL ? 'text-right' : ''}`}>
                    <td className="p-4">
                      <div className="font-medium">{deal.title}</div>
                    </td>
                    <td className="p-4 text-muted-foreground">{getClientName(deal.client_id)}</td>
                    <td className="p-4 font-mono"><CurrencyValue value={deal.value?.toLocaleString() || 0} symbol={currencySymbol} isSAR={isSAR} size={14} /></td>
                    <td className="p-4">
                      <Badge className={stages.find(s => s.id === deal.stage)?.color + ' text-white'}>
                        {stages.find(s => s.id === deal.stage)?.label}
                      </Badge>
                    </td>
                    <td className="p-4">{deal.probability}%</td>
                    <td className="p-4 text-muted-foreground text-sm">
                      {deal.expected_close_date ? format(new Date(deal.expected_close_date), 'MMM d, yyyy') : '-'}
                    </td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(deal)}>
                            <Pencil className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(deal.id)}>
                            <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className={`max-w-md ${isRTL ? 'text-right' : ''}`}>
          <DialogHeader>
            <DialogTitle>{editingDeal ? (isRTL ? 'تعديل الصفقة' : 'Edit Deal') : (isRTL ? 'صفقة جديدة' : 'New Deal')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'عنوان الصفقة *' : 'Deal Title *'}</Label>
              <Input 
                value={formData.title || ''}
                onChange={e => setFormData({...formData, title: e.target.value})} 
                placeholder={isRTL ? 'صفقة ترخيص مؤسسي' : 'Enterprise License Deal'}
                className={isRTL ? 'text-right' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'العميل' : 'Client'}</Label>
              <Select value={formData.client_id || ''} onValueChange={v => setFormData({...formData, client_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'اختر العميل' : 'Select client'} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'القيمة ($)' : 'Value ($)'}</Label>
                <Input 
                  type="number"
                  value={formData.value || ''}
                  onChange={e => setFormData({...formData, value: parseFloat(e.target.value) || 0})} 
                  placeholder="50000"
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.deals.stage')}</Label>
                <Select value={formData.stage || 'qualification'} onValueChange={v => setFormData({...formData, stage: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map(stage => (
                      <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.deals.probability')} (%)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  value={formData.probability || ''}
                  onChange={e => setFormData({...formData, probability: parseInt(e.target.value) || 0})} 
                  className={isRTL ? 'text-right' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.deals.expectedClose')}</Label>
                <Input 
                  type="date"
                  value={formData.expected_close_date || ''}
                  onChange={e => setFormData({...formData, expected_close_date: e.target.value})} 
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'الوصف' : 'Description'}</Label>
              <Textarea 
                value={formData.description || ''}
                onChange={e => setFormData({...formData, description: e.target.value})} 
                placeholder={isRTL ? 'تفاصيل الصفقة...' : 'Deal details...'}
                rows={3}
                className={isRTL ? 'text-right' : ''}
              />
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('pages.common.cancel')}</Button>
            <Button onClick={handleSave}>{editingDeal ? (isRTL ? 'تحديث' : 'Update') : (isRTL ? 'إنشاء' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Deals;
